import { Mark } from "@tiptap/core";

/**
 * TipTap mark that wraps selected text in a <span class="ts-{size}">
 * to apply font-size classes defined in common/sections/richtext.css.
 *
 * Sizes are em-relative so they compose with heading vs paragraph bases.
 */

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

const VALID_SIZES = ["s", "m", "l", "xl"];

export const FontSizeMark = Mark.create({
  name: "fontSize",

  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const classes = element.className?.split(" ") || [];
          const sizeClass = classes.find((c) => /^ts-(s|m|l|xl)$/.test(c));
          return sizeClass ? sizeClass.replace("ts-", "") : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.size || !VALID_SIZES.includes(attributes.size)) {
            return {};
          }
          return { class: `ts-${attributes.size}` };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[class]", getAttrs: (el) => {
      const element = el as HTMLElement;
      const hasSize = VALID_SIZES.some((s) => element.classList.contains(`ts-${s}`));
      return hasSize ? {} : false;
    }}];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setFontSize:
        (size: string) =>
        ({ commands }) => {
          if (!VALID_SIZES.includes(size)) return false;
          return commands.setMark(this.name, { size });
        },
      unsetFontSize:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
